<template>
    <new-student-backend-layout
        :page-title="focusArea.title"
        content-bg-color="gray-125"
        header-bg-color="white"
    >
        <template #page_header>
            <div class="flex flex-col gap-4 pt-4">
                <div class="flex flex-col gap-2 lg:flex-row lg:gap-4">
                    <h1 class="text-[26px] font-semibold">
                        {{ focusArea.title }}
                    </h1>
                    <div class="">
                        <StudentTakeQuizButton
                            quiz-type="focus_area"
                            :focus-area="focusArea"
                        />
                    </div>
                </div>
                <div class="overflox-x-scroll">
                    <div class="flex w-[1px] flex-row gap-4">
                        <div class="max-w-xl flex-none">
                            <StudentDashboardWidget
                                title="Incorrect Answers"
                                :value="
                                    studentMissedQuestionsCount.toLocaleString()
                                "
                            >
                                <div class="mt-auto p-3">
                                    <Link
                                        v-if="
                                            $page.props
                                                .studentMissedQuestionsCount
                                        "
                                        :href="
                                            route('student.quiz.start', {
                                                quiz_type: 'missed',
                                                focus_area_id: focusArea.id,
                                            })
                                        "
                                        class="rounded bg-primary-525 px-3 py-2 text-[14px] text-white hover:cursor-pointer hover:bg-primary-625"
                                        >Take Quiz
                                    </Link>
                                    <span
                                        v-else
                                        class="rounded bg-gray-125 px-3 py-2 text-[14px] text-gray-425 hover:cursor-not-allowed"
                                    >
                                        Take Quiz
                                    </span>
                                </div>
                            </StudentDashboardWidget>
                        </div>
                        <div class="max-w-xl flex-none">
                            <StudentDashboardWidget
                                title="Average Score"
                                :value="
                                    Math.round(
                                        studentAverageFocusAreaQuizScore * 100
                                    ) + '%'
                                "
                                reason="Last 100 Questions"
                            />
                        </div>
                        <div class="max-w-xl flex-none">
                            <StudentDashboardWidget
                                title="Questions Answered"
                                :value="
                                    studentTotalFocusAreaQuestionsAnswered.toLocaleString()
                                "
                                reason="All Time"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #content>
            <div class="px-4 py-4 lg:px-10">
                <div class="text-[18px] font-semibold">
                    Study Resources
                    <span
                        class="hidden text-[13px] font-medium text-gray-600 lg:inline-block"
                        >(by category)</span
                    >
                </div>
                <div
                    class="hidden text-[16px] font-medium text-gray-600 lg:block"
                >
                    All study resources for
                    <span class="font-semibold">{{ focusArea.title }}</span>
                    filtered by category
                </div>
                <div class="mt-4 flex flex-col gap-8 lg:mt-8 lg:flex-row">
                    <div class="hidden w-[320px] flex-none lg:block">
                        <StudentDesktopCategorySelector
                            :categories="
                                focusArea.sorted_by_student_average_categories
                            "
                            :init-selected-category="selectedCategory"
                            v-on:category-selected="selectCategory"
                        />
                    </div>
                    <div class="bg-gray-200 lg:hidden lg:bg-white">
                        <div class="mb-2 text-[18px] font-semibold lg:hidden">
                            Filter by category
                            <span class="text-[13px] font-medium text-gray-600"
                                >(sorted by avg score)</span
                            >
                        </div>
                        <StudentMobileCategorySelector
                            :categories="
                                focusArea.sorted_by_student_average_categories
                            "
                            :init-selected-category="selectedCategory"
                            v-on:category-selected="selectCategory"
                        />
                    </div>
                    <div class="flex-grow">
                        <div
                            class="bg-white py-4 lg:shadow"
                            v-if="getEnabledContentTypes().length"
                        >
                            <div
                                class="mb-4 hidden text-[16px] font-semibold text-gray-925 lg:block lg:px-4"
                            >
                                Study Resources
                            </div>
                            <div class="mb-4 w-full lg:px-4">
                                <student-table-content-type-selector
                                    v-on:content-type-selected="
                                        selectContentType
                                    "
                                    :enabled-types="getEnabledContentTypes()"
                                    :init-selected-content-type="
                                        selectedContentType
                                    "
                                />
                            </div>
                            <div
                                v-if="selectedContentType"
                                class="mb-4 flex gap-1 px-4 text-[16px] font-medium text-gray-600"
                            >
                                Showing

                                <div class="font-semibold">
                                    {{
                                        categoryStudyMaterials[
                                            selectedContentType.value
                                        ].length
                                    }}
                                </div>
                                <div
                                    v-if="
                                        categoryStudyMaterials[
                                            selectedContentType.value
                                        ].length === 1
                                    "
                                >
                                    {{
                                        selectedContentType.label.substr(
                                            0,
                                            selectedContentType.label.length - 1
                                        )
                                    }}
                                </div>
                                <div v-else>
                                    {{ selectedContentType.label }}
                                </div>
                                <div>filtered by</div>
                                <div class="font-semibold">
                                    {{ selectedCategory.title }}
                                </div>
                            </div>
                            <div v-if="selectedContentType" class="">
                                <ul>
                                    <template
                                        v-if="
                                            selectedContentType.value ===
                                            'videos'
                                        "
                                    >
                                        <div
                                            class="grid grid-cols-1 justify-start gap-4 border-b border-gray-200 px-4 py-4 hover:cursor-pointer hover:bg-gray-100 md:grid-cols-2"
                                        >
                                            <template
                                                v-for="material in categoryStudyMaterials[
                                                    selectedContentType.value
                                                ]"
                                            >
                                                <Link
                                                    as="li"
                                                    :href="
                                                        getMaterialRoute(
                                                            selectedContentType.value,
                                                            material.id
                                                        )
                                                    "
                                                    class="flex flex-col"
                                                >
                                                    <div
                                                        class="relative mb-2 aspect-video w-full"
                                                        v-if="
                                                            selectedContentType.value ===
                                                            'videos'
                                                        "
                                                    >
                                                        <img
                                                            class="aspect-video w-full"
                                                            :src="
                                                                'https://content.jwplatform.com/thumbs/' +
                                                                material.token +
                                                                '-640.jpg'
                                                            "
                                                        />
                                                        <div
                                                            class="absolute bottom-4 right-4 bg-gray-825 px-2 text-white"
                                                        >
                                                            <media-duration
                                                                :duration-in-seconds="
                                                                    material.duration
                                                                "
                                                            />
                                                        </div>
                                                    </div>
                                                    <div
                                                        class="font-semibold text-primary-525"
                                                    >
                                                        {{ material.title }}
                                                    </div>
                                                    <div
                                                        class="text-[14px] text-gray-700"
                                                    >
                                                        {{
                                                            selectedCategory.title
                                                        }}
                                                    </div>
                                                </Link>
                                            </template>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <template
                                            v-for="material in categoryStudyMaterials[
                                                selectedContentType.value
                                            ]"
                                        >
                                            <Link
                                                as="li"
                                                :href="
                                                    getMaterialRoute(
                                                        selectedContentType.value,
                                                        material.id
                                                    )
                                                "
                                                class="flex justify-start gap-4 border-b border-gray-200 px-4 py-4 hover:cursor-pointer hover:bg-gray-100"
                                            >
                                                <div class="flex flex-col">
                                                    <div
                                                        class="font-semibold text-primary-525"
                                                    >
                                                        {{ material.title }}
                                                    </div>
                                                    <div
                                                        class="text-[14px] text-gray-700"
                                                    >
                                                        {{
                                                            selectedCategory.title
                                                        }}
                                                    </div>
                                                </div>
                                                <div
                                                    class="ml-auto whitespace-nowrap text-[14px] text-gray-700"
                                                >
                                                    <div
                                                        v-if="
                                                            selectedContentType.value ===
                                                            'flashcardStacks'
                                                        "
                                                    >
                                                        {{
                                                            material.flashcards_count
                                                        }}
                                                        items
                                                    </div>
                                                    <div
                                                        v-if="
                                                            selectedContentType.value ===
                                                            'worksheets'
                                                        "
                                                    >
                                                        {{
                                                            material.worksheet_modules_count
                                                        }}
                                                        items
                                                    </div>
                                                    <div
                                                        v-if="
                                                            selectedContentType.value ===
                                                            'studyGuides'
                                                        "
                                                    >
                                                        <!--// tktk value here -->
                                                    </div>
                                                </div>
                                            </Link>
                                        </template>
                                    </template>
                                </ul>
                            </div>
                        </div>
                        <div
                            class="flex items-center justify-center bg-white p-8 text-center text-[16px] text-gray-625 lg:shadow"
                            v-else
                        >
                            No materials at this level found for <br />
                            {{ selectedCategory.title }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </new-student-backend-layout>
</template>

<script>
import NewStudentBackendLayout from "@/_Layouts/NewStudentBackendLayout.vue";
import StudentDashboardWidget from "@/Components/StudentDashboardWidget.vue";
import StudentTableContentTypeSelector from "@/Components/StudentTableContentTypeSelector.vue";
import MediaDuration from "@/Components/MediaDuration.vue";
import StudentTakeQuizButton from "@/Components/StudentTakeQuizButton.vue";
import StudentDesktopCategorySelector from "@/Components/StudentDesktopCategorySelector.vue";
import StudentMobileCategorySelector from "@/Components/StudentMobileCategorySelector.vue";

export default {
    name: "StudentFocusAreaIndex",
    components: {
        StudentMobileCategorySelector,
        StudentDesktopCategorySelector,
        StudentTakeQuizButton,
        MediaDuration,
        StudentTableContentTypeSelector,
        NewStudentBackendLayout,
        StudentDashboardWidget,
    },
    props: {
        focusArea: {
            type: Object,
            default: {},
        },
        studentMissedQuestionsCount: {
            type: [Number, String],
            default: null,
        },
        studentAverageFocusAreaQuizScore: {
            type: [Number, String],
            default: null,
        },
        studentTotalFocusAreaQuestionsAnswered: {
            type: [Number, String],
            default: null,
        },
        selectedCategory: {
            type: Object,
            default: null,
        },
        categoryStudyMaterials: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            selectedContentType: null,
        };
    },
    methods: {
        selectCategory(categoryId) {
            let vm = this;

            vm.$inertia.get(
                route("student.focus-area.index", {
                    focus_area: vm.focusArea.id,
                    category_id: categoryId,
                })
            );
        },
        selectContentType(contentType) {
            this.selectedContentType = contentType;
        },

        getEnabledContentTypes() {
            let enabledTypes = Object.keys(this.categoryStudyMaterials);

            return enabledTypes;
        },
        getMaterialRoute(materialType, materialId) {
            let materialRoute;

            if (materialType === "flashcardStacks") {
                materialRoute = route("student.flashcards.show-stack", {
                    flashcard_stack: materialId,
                });
            } else if (materialType === "videos") {
                materialRoute = route("student.videos.show-video", {
                    video: materialId,
                });
            } else if (materialType === "worksheets") {
                materialRoute = route("student.worksheet.show-worksheet", {
                    worksheet: materialId,
                });
            } else if (materialType === "studyGuides") {
                materialRoute = route("student.study-guides.show-study-guide", {
                    study_guide: materialId,
                });
            }

            return materialRoute;
        },
    },
    mounted() {
        let selectedContentType = null;
        let firstAvailableMaterialTypeKey = Object.keys(
            this.categoryStudyMaterials
        )[0];

        if (firstAvailableMaterialTypeKey == "flashcardStacks") {
            selectedContentType = {
                label: "Flashcards",
                value: "flashcardStacks",
            };
        }

        if (firstAvailableMaterialTypeKey == "videos") {
            selectedContentType = {
                label: "Videos",
                value: "videos",
            };
        }

        if (firstAvailableMaterialTypeKey == "worksheets") {
            selectedContentType = {
                label: "Interactive Exercises",
                value: "worksheets",
            };
        }

        if (firstAvailableMaterialTypeKey == "studyGuides") {
            selectedContentType = {
                label: "Study Guides",
                value: "studyGuides",
            };
        }

        this.selectedContentType = selectedContentType;
    },
};
</script>
